import React, { useState, useEffect } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';

function BioequivalenceFullReplicate() {
  const { state, upsert, audit, currentUser } = useData();
  const toast = useToast();
  
  const [inputs, setInputs] = useState({
    gmr: 0.95,
    cvWR: 35,
    cvWT: 30,
    lower: 0.80,
    upper: 1.25,
    alpha: 0.05,
    power: 0.90,
    seqLayout: 2,
    dropout: 15
  });

  const [result, setResult] = useState(null);
  const [scenarioName, setScenarioName] = useState('Full Replicate BE — ' + new Date().toLocaleDateString());
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [source, setSource] = useState('');
  const [editableJustification, setEditableJustification] = useState('');
  const [activeTab, setActiveTab] = useState('results');
  const [sensitivityResults, setSensitivityResults] = useState(null);
  const [powerCurveData, setPowerCurveData] = useState(null);
  const [sampleSizeCurveData, setSampleSizeCurveData] = useState(null);

  // Statistical engine
  const Stat = {
    pnorm(x) {
      if (!isFinite(x)) return x > 0 ? 1 : 0;
      const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
      const sign = x < 0 ? -1 : 1;
      const ax = Math.abs(x) / Math.SQRT2;
      const t = 1.0 / (1.0 + p * ax);
      const y = 1.0 - (((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-ax * ax));
      return 0.5 * (1.0 + sign * y);
    },
    qnorm(p) {
      if (p <= 0) return -Infinity;
      if (p >= 1) return Infinity;
      const a = [-3.969683028665376e+01, 2.209460984245205e+02, -2.759285104469687e+02, 1.383577518672690e+02, -3.066479806614716e+01, 2.506628277459239e+00];
      const b = [-5.447609879822406e+01, 1.615858368580409e+02, -1.556989798598866e+02, 6.680131188771972e+01, -1.328068155288572e+01];
      const c = [-7.784894002430293e-03, -3.223964580411365e-01, -2.400758277161838e+00, -2.549732539343734e+00, 4.374664141464968e+00, 2.938163982698783e+00];
      const d = [7.784695709041462e-03, 3.224671290700398e-01, 2.445134137142996e+00, 3.754408661907416e+00];
      const plow = 0.02425, phigh = 1 - plow;
      let q, r;
      if (p < plow) {
        q = Math.sqrt(-2 * Math.log(p));
        return (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) / ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
      } else if (p <= phigh) {
        q = p - 0.5;
        r = q * q;
        return (((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q / (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1);
      } else {
        q = Math.sqrt(-2 * Math.log(1 - p));
        return -(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) / ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
      }
    },
    qt(p, df) {
      if (df > 1000) return this.qnorm(p);
      const z = this.qnorm(p);
      const z2 = z * z;
      return z + (z * (z2 + 1)) / (4 * df) + (z * ((5 * z2 + 16) * z2 + 3)) / (96 * df * df) + (z * (((3 * z2 + 19) * z2 + 17) * z2 - 15)) / (384 * df * df * df);
    }
  };

  // Main calculation
  const calculate = () => {
    if (inputs.cvWR <= 0 || inputs.gmr <= 0) {
      toast('GMR and CVwR must be positive.', 'warn');
      return;
    }

    const U = Math.log(inputs.upper);
    const L = Math.log(inputs.lower);
    const lg = Math.log(inputs.gmr);

    if (lg <= L || lg >= U) {
      toast('Assumed GMR lies on/outside the BE limits; design infeasible.', 'warn');
      return;
    }

    const sigmaWR = Math.sqrt(Math.log(1 + Math.pow(inputs.cvWR / 100, 2)));
    const cvT = (inputs.cvWT && inputs.cvWT > 0) ? inputs.cvWT : inputs.cvWR;
    const sigmaWT = Math.sqrt(Math.log(1 + Math.pow(cvT / 100, 2)));
    const delta = Math.min(U - lg, lg - L);

    // Fully replicate: Var ≈ 0.5·(σ_wT² + σ_wR²) / N
    const varFactor = (N) => 0.5 * (sigmaWT * sigmaWT + sigmaWR * sigmaWR) / N;
    const nSeqs = inputs.seqLayout === 4 ? 4 : 2;
    const dfFn = (N) => 3 * N - 4;
    const roundBase = nSeqs;
    const nStart = Math.max(8, 2 * roundBase);

    let N = nStart;
    for (let it = 0; it < 60; it++) {
      const v = varFactor(N);
      if (!isFinite(v) || v <= 0) return null;
      const df = Math.max(1, dfFn(N));
      const ta = Stat.qt(1 - inputs.alpha, df);
      const tb = Stat.qt(inputs.power, df);
      const vTarget = (delta * delta) / Math.pow(ta + tb, 2);

      let Nnew = N;
      if (v > vTarget) {
        while (true) {
          Nnew += roundBase;
          if (varFactor(Nnew) <= vTarget) break;
          if (Nnew > 100000) return null;
        }
      } else {
        while (Nnew - roundBase >= nStart && varFactor(Nnew - roundBase) <= vTarget) {
          Nnew -= roundBase;
        }
      }
      if (Nnew === N) break;
      N = Nnew;
    }

    const perSeq = N / nSeqs;
    const se = Math.sqrt(varFactor(N));
    const dfResidual = dfFn(N);
    const taFinal = Stat.qt(1 - inputs.alpha, Math.max(1, dfResidual));
    const t1 = (U - lg) / se - taFinal;
    const t2 = (lg - L) / se - taFinal;
    const achievedPower = Math.max(0, Math.min(1, Stat.pnorm(t1) + Stat.pnorm(t2) - 1));

    const dropoutRate = inputs.dropout / 100;
    const enrolledPerSeq = Math.ceil(perSeq / (1 - dropoutRate));

    const seqLabels = (nSeqs === 4) ? ['TRTR', 'RTRT', 'TRRT', 'RTTR'] : ['TRTR', 'RTRT'];

    const resultData = {
      nPerSequence: Math.round(perSeq),
      nTotal: N,
      nEnrolledPerSequence: enrolledPerSeq,
      nEnrolledTotal: nSeqs * enrolledPerSeq,
      sigmaWR: sigmaWR,
      sigmaWT: sigmaWT,
      deltaLog: delta,
      se: se,
      dfResidual: dfResidual,
      achievedPower: achievedPower,
      alpha: inputs.alpha,
      dropout: inputs.dropout,
      cvWTUsed: cvT,
      cvWTAssumed: inputs.cvWT === 0,
      nSequences: nSeqs,
      sequences: seqLabels,
      lg: lg,
      U: U,
      L: L,
      formulaSummary: `SE² ≈ ½(σ²_wT + σ²_wR)/N; ${nSeqs}-sequence layout ${seqLabels.join(' / ')} (fully replicate, log-scale TOST)`,
      note: "Fully-replicate planning approximation. If CVwT is not entered, the calculation assumes CVwT = CVwR. Replicate designs are commonly used for highly variable products; scaled BE / RSABE assessment is an additional regulatory layer not implemented in this approximation."
    };

    setResult(resultData);
    setEditableJustification(generateJustification(inputs, resultData));
    generateSensitivity(inputs, resultData);
    generatePowerCurve(inputs, resultData);
    generateSampleSizeCurve(inputs, resultData);
    toast('Calculation complete', 'ok');
  };

  // Generate detailed justification
  const generateJustification = (inputs, result) => {
    const lg = result.lg;
    const U = result.U;
    const L = result.L;
    const cvWTDisplay = result.cvWTAssumed ? `${inputs.cvWR}% (assumed equal to CVwR)` : `${result.cvWTUsed}%`;
    const nSeqs = result.nSequences;
    const seqLabels = result.sequences.join(' / ');
    
    return `
      <h4>1. Study Context and Design</h4>
      <p>
        This bioequivalence sample-size calculation is for a <strong>fully replicate design</strong> 
        (4 periods, ${nSeqs} sequences: ${seqLabels}) comparing Test (T) and Reference (R) formulations.
        The study is designed to demonstrate average bioequivalence (ABE) within the regulatory 
        acceptance limits of <strong>[${inputs.lower}, ${inputs.upper}]</strong> on the log scale.
      </p>
      <p>
        The fully replicate design is the <strong>most efficient design for highly variable drug products (HVDPs)</strong>
        as it replicates both test and reference, enabling:
      </p>
      <ul>
        <li><strong>Estimation of CVwT and CVwR</strong> — within-subject variability of both products</li>
        <li><strong>Reference-Scaled Average Bioequivalence (RSABE)</strong> assessments</li>
        <li><strong>Widened BE limits</strong> for highly variable drugs (75.00–133.33%)</li>
        <li><strong>Maximum efficiency</strong> — each subject provides two comparisons of each product</li>
      </ul>
      <p>
        <strong>Design characteristics:</strong>
        <ul>
          <li><strong>Periods:</strong> 4</li>
          <li><strong>Sequences:</strong> ${nSeqs} (${seqLabels})</li>
          <li><strong>Treatment repetitions:</strong> Both Test and Reference are replicated twice</li>
          <li><strong>Key advantage:</strong> Enables estimation of both CVwT and CVwR</li>
          <li><strong>Primary use:</strong> Highly variable drugs (CV &gt; 30%) with RSABE considerations</li>
        </ul>
      </p>

      <h4>2. Statistical Hypothesis</h4>
      <p>
        Two one-sided tests (TOST) at <strong>α = ${inputs.alpha}</strong> each:
      </p>
      <ul>
        <li><strong>H₀:</strong> ln(T/R) ≤ ln(${inputs.lower}) OR ln(T/R) ≥ ln(${inputs.upper})</li>
        <li><strong>H₁:</strong> ln(${inputs.lower}) &lt; ln(T/R) &lt; ln(${inputs.upper})</li>
      </ul>
      <p>
        The study is powered at <strong>${(inputs.power * 100).toFixed(0)}%</strong> to demonstrate 
        bioequivalence under the assumed GMR of <strong>${inputs.gmr}</strong>.
      </p>

      <h4>3. Assumptions and Input Parameters</h4>
      <table style="width: 100%; font-size: 13px; border-collapse: collapse; margin: 10px 0;">
        <tbody>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px; font-weight: 500;">Parameter</td>
            <td style="padding: 6px 8px; font-weight: 500;">Value</td>
            <td style="padding: 6px 8px; font-weight: 500;">Justification / Source</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">Test/Reference GMR</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${inputs.gmr}</td>
            <td style="padding: 6px 8px; color: var(--ink-3);">${source || 'Based on previous studies or literature'}</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">CVwR (Reference CV%)</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${inputs.cvWR}%</td>
            <td style="padding: 6px 8px; color: var(--ink-3);">${source || 'Based on previous studies or literature'}</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">CVwT (Test CV%)</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${cvWTDisplay}</td>
            <td style="padding: 6px 8px; color: var(--ink-3);">${source || 'Based on previous studies or literature'}</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">BE Limits</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">[${inputs.lower}, ${inputs.upper}]</td>
            <td style="padding: 6px 8px; color: var(--ink-3);">Regulatory standard (CDSCO/EMA/FDA)</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">Significance Level (α)</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${inputs.alpha}</td>
            <td style="padding: 6px 8px; color: var(--ink-3);">Standard for TOST procedure</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">Target Power (1 − β)</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${(inputs.power * 100).toFixed(0)}%</td>
            <td style="padding: 6px 8px; color: var(--ink-3);">Higher power for HVDPs</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">Sequence Layout</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${nSeqs} sequences</td>
            <td style="padding: 6px 8px; color: var(--ink-3);">${nSeqs === 4 ? '4 sequences (TRTR/RTRT/TRRT/RTTR)' : '2 sequences (TRTR/RTRT)'}</td>
          </tr>
          <tr>
            <td style="padding: 6px 8px;">Anticipated Dropout</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${inputs.dropout}%</td>
            <td style="padding: 6px 8px; color: var(--ink-3);">Higher dropout expected for 4-period designs</td>
          </tr>
        </tbody>
      </table>

      <h4>4. Statistical Method &amp; Formula</h4>
      <p>
        The fully replicate design sample size is estimated using the planning approximation 
        from <strong>Chow and Liu (2008)</strong>:
      </p>
      <div style="background: var(--surface-2); padding: 12px 16px; border-radius: 4px; margin: 10px 0; font-family: var(--mono); font-size: 13px; text-align: center; border-left: 4px solid var(--accent-3);">
        SE² ≈ ½ · (σ²<sub>wT</sub> + σ²<sub>wR</sub>) / N
      </div>
      <p>where:</p>
      <ul>
        <li><strong>σ²<sub>wR</sub></strong> = log-scale within-subject variance of the reference product = ln(1 + (CV<sub>wR</sub>/100)²)</li>
        <li><strong>σ²<sub>wT</sub></strong> = log-scale within-subject variance of the test product = ln(1 + (CV<sub>wT</sub>/100)²)</li>
        <li><strong>N</strong> = total number of subjects across all sequences</li>
      </ul>
      <p>
        Both products are replicated twice, giving a variance contribution of 
        ½·(σ²<sub>wT</sub> + σ²<sub>wR</sub>) for the LSMean T-R difference scaled by N.
      </p>

      <h4>5. Calculation Steps</h4>
      <table style="width: 100%; font-size: 13px; border-collapse: collapse; margin: 10px 0;">
        <tbody>
          <tr style="border-bottom: 1px solid var(--line); background: var(--surface-2);">
            <td style="padding: 6px 8px; font-weight: 500;">Step</td>
            <td style="padding: 6px 8px; font-weight: 500;">Calculation</td>
            <td style="padding: 6px 8px; font-weight: 500;">Result</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">1. Convert CVs to log-scale σ</td>
            <td style="padding: 6px 8px; font-family: var(--mono); font-size: 12px;">σ = √(ln(1 + (CV/100)²))</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">σ<sub>wR</sub> = ${result.sigmaWR.toFixed(4)}<br>σ<sub>wT</sub> = ${result.sigmaWT.toFixed(4)}</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">2. Log-transform BE limits</td>
            <td style="padding: 6px 8px; font-family: var(--mono); font-size: 12px;">ln(${inputs.lower}) to ln(${inputs.upper})</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">[${L.toFixed(4)}, ${U.toFixed(4)}]</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">3. Log-transform GMR</td>
            <td style="padding: 6px 8px; font-family: var(--mono); font-size: 12px;">ln(${inputs.gmr})</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${lg.toFixed(4)}</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">4. Distance to nearer BE limit</td>
            <td style="padding: 6px 8px; font-family: var(--mono); font-size: 12px;">δ = min(U − lg, lg − L)</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${result.deltaLog.toFixed(4)}</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">5. Variance factor</td>
            <td style="padding: 6px 8px; font-family: var(--mono); font-size: 12px;">½·(σ²<sub>wT</sub> + σ²<sub>wR</sub>)</td>
            <td style="padding: 6px 8px; font-family: var(--mono);">${(0.5 * (result.sigmaWT * result.sigmaWT + result.sigmaWR * result.sigmaWR)).toFixed(4)}</td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">6. Iterative N search</td>
            <td style="padding: 6px 8px; font-family: var(--mono); font-size: 12px;">N + ${result.nSequences} until variance ≤ target</td>
            <td style="padding: 6px 8px; font-family: var(--mono);"><strong>N = ${result.nTotal}</strong></td>
          </tr>
          <tr style="border-bottom: 1px solid var(--line);">
            <td style="padding: 6px 8px;">7. Per sequence allocation</td>
            <td style="padding: 6px 8px; font-family: var(--mono); font-size: 12px;">N / ${result.nSequences} sequences</td>
            <td style="padding: 6px 8px; font-family: var(--mono);"><strong>${result.nPerSequence} per sequence</strong></td>
          </tr>
          <tr>
            <td style="padding: 6px 8px;">8. Dropout inflation</td>
            <td style="padding: 6px 8px; font-family: var(--mono); font-size: 12px;">n / (1 − ${inputs.dropout}/100)</td>
            <td style="padding: 6px 8px; font-family: var(--mono);"><strong>${result.nEnrolledPerSequence} per sequence</strong></td>
          </tr>
        </tbody>
      </table>

      <h4>6. Interpretation of Results</h4>
      <p>
        A total of <strong>${result.nEnrolledTotal}</strong> subjects will be randomized 
        (approximately <strong>${result.nEnrolledPerSequence}</strong> per sequence) to 
        provide <strong>${(result.achievedPower * 100).toFixed(1)}%</strong> power to 
        demonstrate average bioequivalence within [${inputs.lower}, ${inputs.upper}] 
        under the assumed GMR of ${inputs.gmr}, CVwR of ${inputs.cvWR}%, and CVwT of 
        ${cvWTDisplay}.
      </p>
      <p>
        The fully replicate design is the <strong>most powerful design for HVDPs</strong> because:
      </p>
      <ul>
        <li><strong>Both products replicated:</strong> Enables estimation of CVwT and CVwR</li>
        <li><strong>RSABE ready:</strong> Supports Reference-Scaled Average Bioequivalence assessments</li>
        <li><strong>Maximum efficiency:</strong> Each subject provides four observations (two of each product)</li>
        <li><strong>Widened limits:</strong> Supports 75.00–133.33% limits for HVDPs</li>
      </ul>

      <h4>7. Sensitivity Analysis</h4>
      <p>
        The sample size is sensitive to the assumed GMR, CVwR, and CVwT. 
        A sensitivity analysis should be performed varying these parameters.
      </p>

      <h4>8. Limitations &amp; Review Note</h4>
      <p>
        This is a planning approximation based on Chow and Liu (2008). Final sample size 
        should be confirmed using specialized BE software (e.g., SAS/PowerTOST, nQuery, PASS).
      </p>
      <p>
        For Reference-Scaled ABE (RSABE) applications, additional regulatory requirements 
        apply including the estimation of the within-subject standard deviation of the 
        reference product and the appropriate scaling factor.
      </p>
    `;
  };

  // Generate sensitivity analysis
  const generateSensitivity = (inputs, result) => {
    const params = [
      { id: 'cvWR', label: 'CVwR %', values: [20, 25, 30, 35, 40, 45, 50, 55] },
      { id: 'cvWT', label: 'CVwT %', values: [15, 20, 25, 30, 35, 40, 45, 50] },
      { id: 'gmr', label: 'GMR', values: [0.85, 0.88, 0.90, 0.93, 0.95, 0.97, 0.99, 1.00] },
      { id: 'power', label: 'Target Power', values: [0.70, 0.75, 0.80, 0.85, 0.90, 0.95] },
      { id: 'dropout', label: 'Dropout %', values: [0, 5, 10, 15, 20, 25, 30] }
    ];

    const results = params.map(param => {
      const rows = [];
      for (const v of param.values) {
        const testInputs = { ...inputs, [param.id]: v };
        const r = runCalculation(testInputs);
        if (r) {
          rows.push({
            paramValue: v,
            nTotal: r.nTotal,
            nEnrolled: r.nEnrolledTotal,
            power: r.achievedPower
          });
        }
      }
      return { ...param, rows };
    });

    setSensitivityResults(results);
  };

  const runCalculation = (inputs) => {
    const U = Math.log(inputs.upper);
    const L = Math.log(inputs.lower);
    const lg = Math.log(inputs.gmr);

    if (lg <= L || lg >= U) return null;

    const sigmaWR = Math.sqrt(Math.log(1 + Math.pow(inputs.cvWR / 100, 2)));
    const cvT = (inputs.cvWT && inputs.cvWT > 0) ? inputs.cvWT : inputs.cvWR;
    const sigmaWT = Math.sqrt(Math.log(1 + Math.pow(cvT / 100, 2)));
    const delta = Math.min(U - lg, lg - L);

    const varFactor = (N) => 0.5 * (sigmaWT * sigmaWT + sigmaWR * sigmaWR) / N;
    const nSeqs = inputs.seqLayout === 4 ? 4 : 2;
    const dfFn = (N) => 3 * N - 4;
    const roundBase = nSeqs;
    const nStart = Math.max(8, 2 * roundBase);

    let N = nStart;
    for (let it = 0; it < 60; it++) {
      const v = varFactor(N);
      if (!isFinite(v) || v <= 0) return null;
      const df = Math.max(1, dfFn(N));
      const ta = Stat.qt(1 - inputs.alpha, df);
      const tb = Stat.qt(inputs.power, df);
      const vTarget = (delta * delta) / Math.pow(ta + tb, 2);

      let Nnew = N;
      if (v > vTarget) {
        while (true) {
          Nnew += roundBase;
          if (varFactor(Nnew) <= vTarget) break;
          if (Nnew > 100000) return null;
        }
      } else {
        while (Nnew - roundBase >= nStart && varFactor(Nnew - roundBase) <= vTarget) {
          Nnew -= roundBase;
        }
      }
      if (Nnew === N) break;
      N = Nnew;
    }

    const perSeq = N / nSeqs;
    const se = Math.sqrt(varFactor(N));
    const dfResidual = dfFn(N);
    const taFinal = Stat.qt(1 - inputs.alpha, Math.max(1, dfResidual));
    const t1 = (U - lg) / se - taFinal;
    const t2 = (lg - L) / se - taFinal;
    const achievedPower = Math.max(0, Math.min(1, Stat.pnorm(t1) + Stat.pnorm(t2) - 1));

    const dropoutRate = inputs.dropout / 100;
    const enrolledPerSeq = Math.ceil(perSeq / (1 - dropoutRate));

    return {
      nTotal: N,
      nEnrolledTotal: nSeqs * enrolledPerSeq,
      achievedPower: achievedPower
    };
  };

  const generatePowerCurve = (inputs, result) => {
    const points = [];
    const baseN = result.nTotal;
    const minN = Math.max(8, Math.round(baseN * 0.2));
    const maxN = Math.max(minN + 1, Math.round(baseN * 2.5));
    const steps = 30;
    const dx = (maxN - minN) / steps;

    const U = Math.log(inputs.upper);
    const L = Math.log(inputs.lower);
    const lg = Math.log(inputs.gmr);
    const delta = Math.min(U - lg, lg - L);
    const sigmaWR = Math.sqrt(Math.log(1 + Math.pow(inputs.cvWR / 100, 2)));
    const cvT = (inputs.cvWT && inputs.cvWT > 0) ? inputs.cvWT : inputs.cvWR;
    const sigmaWT = Math.sqrt(Math.log(1 + Math.pow(cvT / 100, 2)));
    const nSeqs = inputs.seqLayout === 4 ? 4 : 2;
    const varFactor = (N) => 0.5 * (sigmaWT * sigmaWT + sigmaWR * sigmaWR) / N;
    const dfFn = (N) => 3 * N - 4;

    for (let i = 0; i <= steps; i++) {
      const N = Math.round(minN + dx * i);
      if (N < 8) continue;
      const se = Math.sqrt(varFactor(N));
      const df = Math.max(1, dfFn(N));
      const ta = Stat.qt(1 - inputs.alpha, df);
      const t1 = delta / se - ta;
      const power = Math.max(0, Math.min(1, Stat.pnorm(t1) + Stat.pnorm(t1) - 1));
      points.push({ N, power });
    }

    setPowerCurveData(points);
  };

  const generateSampleSizeCurve = (inputs, result) => {
    const points = [];
    const cvWRValues = [15, 20, 25, 30, 35, 40, 45, 50, 55];

    for (const cv of cvWRValues) {
      const testInputs = { ...inputs, cvWR: cv };
      const r = runCalculation(testInputs);
      if (r) {
        points.push({ cvWR: cv, nTotal: r.nTotal, nEnrolled: r.nEnrolledTotal });
      }
    }

    setSampleSizeCurveData(points);
  };

  // SVG Chart Generator
  const renderSVGChart = ({ data, xKey, yKey, xLabel, yLabel, width = 560, height = 280, targetValue = null }) => {
    if (!data || data.length < 2) return null;

    const margin = { top: 20, right: 30, bottom: 40, left: 50 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const xValues = data.map(d => d[xKey]);
    const yValues = data.map(d => d[yKey]);
    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = Math.min(0, ...yValues);
    const yMax = Math.max(...yValues) * 1.1;

    const xScale = (v) => margin.left + ((v - xMin) / (xMax - xMin)) * chartWidth;
    const yScale = (v) => margin.top + chartHeight - ((v - yMin) / (yMax - yMin)) * chartHeight;

    let svg = `<svg viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" font-family="ui-sans-serif, system-ui, sans-serif" font-size="11">`;

    // Grid lines
    const yTicks = [0, 0.25, 0.5, 0.75, 1];
    for (const tick of yTicks) {
      const y = yScale(tick);
      svg += `<line x1="${margin.left}" x2="${width - margin.right}" y1="${y}" y2="${y}" stroke="var(--line)" stroke-width="0.5" stroke-dasharray="3,3"/>`;
    }

    // Axes
    svg += `<line x1="${margin.left}" x2="${width - margin.right}" y1="${margin.top + chartHeight}" y2="${margin.top + chartHeight}" stroke="var(--ink)" stroke-width="1"/>`;
    svg += `<line x1="${margin.left}" x2="${margin.left}" y1="${margin.top}" y2="${margin.top + chartHeight}" stroke="var(--ink)" stroke-width="1"/>`;

    // Y-axis labels
    for (const tick of yTicks) {
      const y = yScale(tick);
      svg += `<text x="${margin.left - 8}" y="${y + 4}" text-anchor="end" fill="var(--ink-3)" font-size="10">${tick.toFixed(2)}</text>`;
    }

    // X-axis labels
    const step = Math.max(1, Math.floor(data.length / 8));
    for (let i = 0; i < data.length; i += step) {
      const x = xScale(data[i][xKey]);
      svg += `<text x="${x}" y="${margin.top + chartHeight + 18}" text-anchor="middle" fill="var(--ink-3)" font-size="9">${data[i][xKey].toFixed(1)}</text>`;
    }

    // X-axis label
    svg += `<text x="${margin.left + chartWidth / 2}" y="${height - 4}" text-anchor="middle" fill="var(--ink-2)" font-size="10" font-weight="500">${xLabel}</text>`;

    // Y-axis label
    svg += `<text transform="translate(14,${margin.top + chartHeight / 2}) rotate(-90)" text-anchor="middle" fill="var(--ink-2)" font-size="10" font-weight="500">${yLabel}</text>`;

    // Data line
    let path = '';
    for (let i = 0; i < data.length; i++) {
      const x = xScale(data[i][xKey]);
      const y = yScale(data[i][yKey]);
      path += (i === 0 ? 'M' : 'L') + x + ',' + y;
    }
    svg += `<path d="${path}" fill="none" stroke="var(--accent-3)" stroke-width="2"/>`;

    // Data points
    for (const d of data) {
      const x = xScale(d[xKey]);
      const y = yScale(d[yKey]);
      svg += `<circle cx="${x}" cy="${y}" r="3" fill="var(--accent-3)"/>`;
    }

    // Target line
    if (targetValue !== null && targetValue > 0) {
      const y = yScale(targetValue);
      svg += `<line x1="${margin.left}" x2="${width - margin.right}" y1="${y}" y2="${y}" stroke="var(--warn)" stroke-width="1.5" stroke-dasharray="6,4"/>`;
      svg += `<text x="${width - margin.right - 4}" y="${y - 4}" text-anchor="end" fill="var(--warn)" font-size="9" font-weight="500">Target</text>`;
    }

    svg += `</svg>`;
    return svg;
  };

  const saveScenario = () => {
    if (!result) {
      toast('Calculate first before saving.', 'warn');
      return;
    }

    const scenario = {
      id: 'be-full-' + Date.now(),
      name: scenarioName,
      methodKey: 'be_fully_replicate',
      inputs: { ...inputs },
      result: { ...result },
      justification: editableJustification,
      source: source,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: currentUser?.name || 'Unknown'
    };

    upsert('bioequivalenceScenarios', scenario);
    audit('BE_FULL_CREATE', scenarioName, `GMR=${inputs.gmr}, CVwR=${inputs.cvWR}%, N=${result.nEnrolledTotal}`, {
      scenarioId: scenario.id,
      userId: currentUser?.id
    });
    toast('Bioequivalence scenario saved!', 'ok');
    setSavedScenarios([scenario, ...savedScenarios]);
  };

  const loadScenario = (scenario) => {
    setInputs(scenario.inputs);
    setResult(scenario.result);
    setScenarioName(scenario.name);
    setEditableJustification(scenario.justification || generateJustification(scenario.inputs, scenario.result));
    setSource(scenario.source || '');
    generateSensitivity(scenario.inputs, scenario.result);
    generatePowerCurve(scenario.inputs, scenario.result);
    generateSampleSizeCurve(scenario.inputs, scenario.result);
    toast('Scenario loaded', 'ok');
  };

  const deleteScenario = (id) => {
    setSavedScenarios(savedScenarios.filter(s => s.id !== id));
    audit('BE_FULL_DELETE', id, 'Scenario deleted', { scenarioId: id });
    toast('Scenario deleted', 'ok');
  };

  const resetToDefaults = () => {
    setInputs({
      gmr: 0.95,
      cvWR: 35,
      cvWT: 30,
      lower: 0.80,
      upper: 1.25,
      alpha: 0.05,
      power: 0.90,
      seqLayout: 2,
      dropout: 15
    });
    setResult(null);
    setEditableJustification('');
    setSensitivityResults(null);
    setPowerCurveData(null);
    setSampleSizeCurveData(null);
    toast('Reset to defaults', 'ok');
  };

  const copyJustification = () => {
    if (!editableJustification) {
      toast('No justification to copy', 'warn');
      return;
    }
    const text = editableJustification.replace(/<[^>]+>/g, '');
    navigator.clipboard?.writeText(text).then(() => {
      toast('Justification copied to clipboard', 'ok');
    }).catch(() => {
      toast('Copy failed', 'err');
    });
  };

  const copySAPParagraph = () => {
    if (!result) {
      toast('Calculate first', 'warn');
      return;
    }
    const cvWTDisplay = result.cvWTAssumed ? `CVwT assumed equal to CVwR (${inputs.cvWR}%)` : `CVwT = ${result.cvWTUsed}%`;
    const nSeqs = result.nSequences;
    const seqLabels = result.sequences.join(' / ');
    const para = `A total of ${result.nEnrolledTotal} subjects (approximately ${result.nEnrolledPerSequence} per sequence in a ${nSeqs}-sequence fully replicate design with sequences ${seqLabels}) will be enrolled (assuming ${inputs.dropout}% dropout) to provide approximately ${(result.achievedPower * 100).toFixed(1)}% power to demonstrate average bioequivalence within [${inputs.lower}, ${inputs.upper}] for a Test/Reference GMR of ${inputs.gmr}, with CVwR = ${inputs.cvWR}% and ${cvWTDisplay}, at a one-sided significance level of ${inputs.alpha}. The fully replicate design enables estimation of within-subject variability for both products, which is essential for highly variable drug products (HVDPs) and Reference-Scaled ABE (RSABE) assessments. Final sample size should be confirmed with specialized BE software.`;
    navigator.clipboard?.writeText(para).then(() => {
      toast('SAP paragraph copied', 'ok');
    }).catch(() => {
      toast('Copy failed', 'err');
    });
  };

  useEffect(() => {
    if (state.bioequivalenceScenarios) {
      const fullScenarios = state.bioequivalenceScenarios
        .filter(s => s.methodKey === 'be_fully_replicate')
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setSavedScenarios(fullScenarios);
    }
  }, [state.bioequivalenceScenarios]);

  return (
    <div className="bioequivalence-module">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Bioequivalence / PK</div>
          <h1 className="page-title">Bioequivalence — Fully Replicate (TRTR/RTRT)</h1>
          <p className="page-sub">
            Both test and reference replicated. Enables estimation of within-subject 
            variability for both products. Supports 2-sequence (TRTR/RTRT) or 
            4-sequence (TRTR/RTRT/TRRT/RTTR) layouts.
            <span className="tag tag-warn tag-dot" style={{ marginLeft: 8 }}>Pre-validation</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-primary" onClick={calculate}>
            <i className="ti ti-calculator" /> Calculate
          </button>
          <button className="btn" onClick={saveScenario}>
            <i className="ti ti-save" /> Save
          </button>
          <button className="btn btn-ghost" onClick={resetToDefaults}>
            <i className="ti ti-refresh" /> Reset
          </button>
        </div>
      </div>

      <div className="alert info" style={{ marginBottom: 16 }}>
        <i className="ti ti-info-circle" />
        <strong>Fully Replicate Design:</strong> 4 periods, both products replicated twice.
        Maximum efficiency for highly variable drugs. Enables CVwT and CVwR estimation.
      </div>

      <div className="row mb-4">
        <div className="col" style={{ maxWidth: 440 }}>
          <div className="card card-pad">
            <div className="card-title">
              <i className="ti ti-settings" /> Input Parameters
            </div>

            <div className="field">
              <label className="field-label">Scenario Name</label>
              <input
                type="text"
                value={scenarioName}
                onChange={(e) => setScenarioName(e.target.value)}
                placeholder="Enter scenario name..."
              />
            </div>

            <div className="field">
              <label className="field-label">Assumed Test/Reference GMR</label>
              <input
                type="number"
                step="0.01"
                min="0.5"
                max="2"
                value={inputs.gmr}
                onChange={(e) => setInputs({ ...inputs, gmr: parseFloat(e.target.value) || 0.95 })}
              />
              <div className="field-hint">Geometric mean ratio (Test / Reference)</div>
            </div>

            <div className="field">
              <label className="field-label">CVwR — Reference CV (%)</label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={inputs.cvWR}
                onChange={(e) => setInputs({ ...inputs, cvWR: parseFloat(e.target.value) || 35 })}
              />
              <div className="field-hint">Within-subject CV of the reference product</div>
            </div>

            <div className="field">
              <label className="field-label">CVwT — Test CV (%)</label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={inputs.cvWT}
                onChange={(e) => setInputs({ ...inputs, cvWT: parseFloat(e.target.value) || 30 })}
              />
              <div className="field-hint">Within-subject CV of the test product</div>
            </div>

            <div className="field-row">
              <div className="field">
                <label className="field-label">Lower BE Limit</label>
                <input
                  type="number"
                  step="0.005"
                  min="0.5"
                  max="1"
                  value={inputs.lower}
                  onChange={(e) => setInputs({ ...inputs, lower: parseFloat(e.target.value) || 0.80 })}
                />
              </div>
              <div className="field">
                <label className="field-label">Upper BE Limit</label>
                <input
                  type="number"
                  step="0.005"
                  min="1"
                  max="2"
                  value={inputs.upper}
                  onChange={(e) => setInputs({ ...inputs, upper: parseFloat(e.target.value) || 1.25 })}
                />
              </div>
            </div>

            <div className="field-row">
              <div className="field">
                <label className="field-label">Alpha (α)</label>
                <input
                  type="number"
                  step="0.005"
                  min="0.001"
                  max="0.5"
                  value={inputs.alpha}
                  onChange={(e) => setInputs({ ...inputs, alpha: parseFloat(e.target.value) || 0.05 })}
                />
                <div className="field-hint">One-sided significance level per TOST</div>
              </div>
              <div className="field">
                <label className="field-label">Target Power</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.5"
                  max="0.999"
                  value={inputs.power}
                  onChange={(e) => setInputs({ ...inputs, power: parseFloat(e.target.value) || 0.90 })}
                />
                <div className="field-hint">Higher power recommended for HVDPs</div>
              </div>
            </div>

            <div className="field-row">
              <div className="field">
                <label className="field-label">Sequence Layout</label>
                <select
                  value={inputs.seqLayout}
                  onChange={(e) => setInputs({ ...inputs, seqLayout: parseInt(e.target.value) })}
                >
                  <option value={2}>2 sequences (TRTR/RTRT)</option>
                  <option value={4}>4 sequences (TRTR/RTRT/TRRT/RTTR)</option>
                </select>
                <div className="field-hint">More sequences may improve balance</div>
              </div>
              <div className="field">
                <label className="field-label">Dropout (%)</label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="95"
                  value={inputs.dropout}
                  onChange={(e) => setInputs({ ...inputs, dropout: parseFloat(e.target.value) || 15 })}
                />
                <div className="field-hint">Higher dropout expected for 4-period designs</div>
              </div>
            </div>

            <div className="field" style={{ marginTop: 8 }}>
              <label className="field-label">Assumption Source</label>
              <textarea
                rows="2"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="e.g. Previous study data, literature, regulatory guidance..."
                style={{ fontSize: 12 }}
              />
              <div className="field-hint">Appears in the saved scenario justification</div>
            </div>
          </div>
        </div>

        <div className="col">
          {result ? (
            <div className="card card-pad">
              <div className="card-title">
                <i className="ti ti-chart-bar" /> Results
              </div>

              <div className="metric-strip" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 16 }}>
                <div className="metric-cell">
                  <div className="metric-label">Per Sequence</div>
                  <div className="metric-value">{result.nPerSequence}</div>
                  <div className="metric-foot">evaluable subjects</div>
                </div>
                <div className="metric-cell">
                  <div className="metric-label">Total Evaluable</div>
                  <div className="metric-value" style={{ color: 'var(--accent-3)' }}>{result.nTotal}</div>
                  <div className="metric-foot">{result.nSequences} sequences · 4 periods</div>
                </div>
                <div className="metric-cell" style={{ borderRight: 'none' }}>
                  <div className="metric-label">Total Enrolled</div>
                  <div className="metric-value" style={{ color: 'var(--ok)' }}>{result.nEnrolledTotal}</div>
                  <div className="metric-foot">with {result.dropout}% dropout</div>
                </div>
              </div>

              <div className="divider" />

              <div className="field-row-3">
                <div>
                  <div className="tiny muted uc">σwR</div>
                  <div className="sm bold">{result.sigmaWR.toFixed(4)}</div>
                  <div className="tiny muted">(CVwR = {inputs.cvWR}%)</div>
                </div>
                <div>
                  <div className="tiny muted uc">σwT</div>
                  <div className="sm bold">{result.sigmaWT.toFixed(4)}</div>
                  <div className="tiny muted">(CVwT = {result.cvWTUsed}%)</div>
                </div>
                <div>
                  <div className="tiny muted uc">Achieved Power</div>
                  <div className="sm bold" style={{ color: 'var(--ok)' }}>{(result.achievedPower * 100).toFixed(1)}%</div>
                  <div className="tiny muted">Target: {(inputs.power * 100).toFixed(0)}%</div>
                </div>
              </div>

              <div className="divider" />

              <div className="field-row-3">
                <div>
                  <div className="tiny muted uc">SE (log scale)</div>
                  <div className="sm bold mono">{result.se.toFixed(4)}</div>
                </div>
                <div>
                  <div className="tiny muted uc">Residual df</div>
                  <div className="sm bold mono">{result.dfResidual}</div>
                </div>
                <div>
                  <div className="tiny muted uc">δ (log scale)</div>
                  <div className="sm bold mono">{result.deltaLog.toFixed(4)}</div>
                </div>
              </div>

              <div className="alert info" style={{ marginTop: 12 }}>
                <div className="mono xs">{result.formulaSummary}</div>
                <div className="tiny muted" style={{ marginTop: 4 }}>
                  Sequences: {result.sequences.join(' / ')}
                  {result.cvWTAssumed && ' · CVwT assumed equal to CVwR'}
                </div>
              </div>

              {result.note && (
                <div className="alert warn" style={{ marginTop: 8 }}>
                  <i className="ti ti-info-circle" /> {result.note}
                </div>
              )}
            </div>
          ) : (
            <div className="card card-pad">
              <div className="empty">
                <i className="ti ti-calculator" />
                <div className="empty-title">Ready to calculate</div>
                <div className="tiny muted">Enter parameters and click "Calculate"</div>
              </div>
            </div>
          )}

          {savedScenarios.length > 0 && (
            <div className="card card-pad mt-3">
              <div className="card-title">
                <i className="ti ti-files" /> Saved Scenarios ({savedScenarios.length})
              </div>
              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                {savedScenarios.map((s, idx) => (
                  <div key={s.id || idx} className="flex between center" style={{ padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
                    <div>
                      <div className="sm bold">{s.name}</div>
                      <div className="tiny muted">GMR={s.inputs.gmr}, CVwR={s.inputs.cvWR}%, N={s.result.nEnrolledTotal}</div>
                    </div>
                    <div className="flex gap-2">
                      <button className="btn btn-xs btn-ghost" onClick={() => loadScenario(s)} title="Load scenario">
                        <i className="ti ti-eye" />
                      </button>
                      <button className="btn btn-xs btn-ghost" style={{ color: 'var(--danger)' }} onClick={() => deleteScenario(s.id)} title="Delete scenario">
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {result && (
        <div>
          <div className="tabs">
            <button 
              className={`tab ${activeTab === 'results' ? 'active' : ''}`}
              onClick={() => setActiveTab('results')}
            >
              <i className="ti ti-file-text" /> Justification
            </button>
            <button 
              className={`tab ${activeTab === 'formula' ? 'active' : ''}`}
              onClick={() => setActiveTab('formula')}
            >
              <i className="ti ti-math" /> Formula & Methodology
            </button>
            <button 
              className={`tab ${activeTab === 'sensitivity' ? 'active' : ''}`}
              onClick={() => setActiveTab('sensitivity')}
            >
              <i className="ti ti-chart-line" /> Sensitivity Analysis
            </button>
            <button 
              className={`tab ${activeTab === 'curves' ? 'active' : ''}`}
              onClick={() => setActiveTab('curves')}
            >
              <i className="ti ti-chart-areas" /> Power & Sample Size Curves
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'results' && (
              <div className="card card-pad">
                <div className="alert info" style={{ marginBottom: 14 }}>
                  <i className="ti ti-edit" /> Edit any sentence below — the calculated values table 
                  (above) is preserved separately for traceability.
                </div>
                <div 
                  className="justification" 
                  contentEditable="true"
                  dangerouslySetInnerHTML={{ __html: editableJustification }}
                  onInput={(e) => setEditableJustification(e.target.innerHTML)}
                  style={{ 
                    minHeight: 200,
                    padding: '12px 16px',
                    border: '1px solid var(--line)',
                    borderRadius: 4,
                    outline: 'none',
                    background: 'var(--surface)'
                  }}
                />
                <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-sm btn-ghost" onClick={copyJustification}>
                    <i className="ti ti-copy" /> Copy justification text
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={copySAPParagraph}>
                    <i className="ti ti-file-text" /> Copy SAP paragraph only
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={() => {
                    if (result) {
                      const just = generateJustification(inputs, result);
                      setEditableJustification(just);
                      toast('Justification reset to generated version', 'ok');
                    }
                  }}>
                    <i className="ti ti-refresh" /> Reset to generated
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'formula' && (
              <div className="card card-pad">
                <h3 style={{ fontFamily: 'var(--display)', marginBottom: 12 }}>Formula & Methodology</h3>
                
                <h4>Statistical Model</h4>
                <p>The fully replicate design uses the following ANOVA model for the log-transformed PK parameters:</p>
                <div style={{ background: 'var(--surface-2)', padding: '12px 16px', borderRadius: 4, margin: '10px 0', fontFamily: 'var(--mono)', fontSize: 13 }}>
                  Y<sub>ijk</sub> = μ + S<sub>i</sub> + P<sub>j</sub> + T<sub>k</sub> + ε<sub>ijk</sub>
                </div>
                <ul>
                  <li><strong>Y<sub>ijk</sub></strong> = log-transformed PK parameter for subject i, period j, treatment k</li>
                  <li><strong>μ</strong> = overall mean</li>
                  <li><strong>S<sub>i</sub></strong> = random subject effect</li>
                  <li><strong>P<sub>j</sub></strong> = fixed period effect</li>
                  <li><strong>T<sub>k</sub></strong> = fixed treatment effect</li>
                  <li><strong>ε<sub>ijk</sub></strong> = residual error (within-subject variability)</li>
                </ul>

                <h4>Sample Size Formula</h4>
                <div style={{ background: 'var(--surface-2)', padding: '12px 16px', borderRadius: 4, margin: '10px 0', fontFamily: 'var(--mono)', fontSize: 14, textAlign: 'center', borderLeft: '4px solid var(--accent-3)' }}>
                  SE² = ½ · (σ²<sub>wT</sub> + σ²<sub>wR</sub>) / N
                </div>

                <h4>Variance Components</h4>
                <ul>
                  <li><strong>σ²<sub>wR</sub> = ln(1 + (CV<sub>wR</sub>/100)²)</strong> — reference variance</li>
                  <li><strong>σ²<sub>wT</sub> = ln(1 + (CV<sub>wT</sub>/100)²)</strong> — test variance</li>
                </ul>

                <div className="alert info" style={{ marginTop: 12 }}>
                  <strong>Reference:</strong> Chow, S.C., &amp; Liu, J.P. (2008). 
                  Design and Analysis of Bioavailability and Bioequivalence Studies. 
                  Chapman &amp; Hall/CRC, 3rd Edition.
                </div>
              </div>
            )}

            {activeTab === 'sensitivity' && sensitivityResults && (
              <div className="card card-pad">
                <h3 style={{ fontFamily: 'var(--display)', marginBottom: 12 }}>Sensitivity Analysis</h3>
                <p className="muted" style={{ marginBottom: 16 }}>
                  Varying key assumptions shows how the sample size changes under alternative scenarios.
                </p>

                {sensitivityResults.map((param, idx) => (
                  <div key={idx} style={{ marginBottom: 20 }}>
                    <h4>{param.label}</h4>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="tbl">
                        <thead>
                          <tr>
                            <th>{param.label}</th>
                            <th className="num">Total Evaluable (N)</th>
                            <th className="num">Total Enrolled</th>
                            <th className="num">Achieved Power</th>
                          </tr>
                        </thead>
                        <tbody>
                          {param.rows.map((row, i) => (
                            <tr key={i} style={row.nTotal === result?.nTotal ? { background: 'var(--accent-soft)' } : {}}>
                              <td><strong>{row.paramValue}</strong></td>
                              <td className="num">{row.nTotal}</td>
                              <td className="num">{row.nEnrolled}</td>
                              <td className="num">{(row.power * 100).toFixed(1)}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}

                <div className="alert warn" style={{ marginTop: 12 }}>
                  <i className="ti ti-alert-triangle" />
                  <strong>Interpretation:</strong> The fully replicate design is robust to CV differences 
                  between test and reference. Sample size is driven by the larger of the two CVs.
                </div>
              </div>
            )}

            {activeTab === 'curves' && (
              <div className="card card-pad">
                <h3 style={{ fontFamily: 'var(--display)', marginBottom: 12 }}>Power & Sample Size Curves</h3>

                {powerCurveData && powerCurveData.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <h4>Power vs. Sample Size</h4>
                    <div 
                      dangerouslySetInnerHTML={{ 
                        __html: renderSVGChart({
                          data: powerCurveData,
                          xKey: 'N',
                          yKey: 'power',
                          xLabel: 'Total Sample Size (N)',
                          yLabel: 'Power',
                          targetValue: inputs.power
                        }) 
                      }} 
                    />
                    <div className="tiny muted" style={{ marginTop: 4 }}>
                      Target power: {(inputs.power * 100).toFixed(0)}% (dashed line) · Current N = {result?.nTotal || 0}
                    </div>
                  </div>
                )}

                {sampleSizeCurveData && sampleSizeCurveData.length > 0 && (
                  <div>
                    <h4>Sample Size vs. CVwR</h4>
                    <div 
                      dangerouslySetInnerHTML={{ 
                        __html: renderSVGChart({
                          data: sampleSizeCurveData,
                          xKey: 'cvWR',
                          yKey: 'nTotal',
                          xLabel: 'CVwR (%)',
                          yLabel: 'Total Sample Size (N)',
                          targetValue: result?.nTotal || null
                        }) 
                      }} 
                    />
                    <div className="tiny muted" style={{ marginTop: 4 }}>
                      Current CVwR: {inputs.cvWR}% · Current N = {result?.nTotal || 0} (dashed line)
                    </div>
                    <div className="tiny muted">
                      <strong>Interpretation:</strong> The fully replicate design is efficient for HVDPs.
                      For CVwR &gt; 40%, this design provides substantial efficiency gains over 2×2 crossover.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BioequivalenceFullReplicate;